import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    display: 'flex',
  },
  label: {
    fontFamily: 'ProximaNova-Bold',
  },
  buttons: {
    flexDirection: 'row',
  },
  optionLabelWrapper: {
    fontSize: 14,
    marginRight: spacing(5),
    '& svg:not($optionIcon)': {
      width: 16,
      height: 16,
    },
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  optionIcon: {
    marginLeft: spacing(1),
  },
  optionLabelText: {
    marginLeft: spacing(1),
  },
  labelTextDisabled: {
    color: `${primary.bluish7} !important`,
    userSelect: 'none',
  },
  disabledIcon: {
    fill: primary.bluish7,
  },
  disabledChecked: {
    backgroundColor: primary.white,
    '& path': {
      fill: primary.bluish7,
    },
  },
}));

const CustomRadioGroup = ({
  className = null,
  customLabelClass = null,
  customLabelWrapperClass = null,
  customOptionLabelWrapperClass = null,
  customRadioClass = null,
  label = '',
  options,
  value,
  onChange,
  isDisabled = false,
}) => {
  const classes = useStyles();

  const handleChange = e => {
    const newValue = e.target.value;

    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const renderOptionLabel = option => {
    const {
      label: optionLabel,
      icon: Icon,
      description: optionDescription,
    } = option;

    return (
      <span
        className={classNames(classes.optionLabel, customLabelWrapperClass)}
      >
        {Icon && (
          <Icon
            className={classNames(classes.optionIcon, {
              [classes.disabledIcon]: isDisabled,
            })}
          />
        )}
        <Typography
          className={classNames(
            classes.optionLabelText,
            {
              [classes.labelTextDisabled]: isDisabled,
            },
            customLabelClass
          )}
          variant="body2"
          component="span"
        >
          {optionLabel}
        </Typography>
        {optionDescription && (
          <Typography
            className={classNames(classes.optionLabelText, {
              [classes.labelTextDisabled]: isDisabled,
            })}
            variant="body2"
            component="span"
          >
            {optionDescription}
          </Typography>
        )}
      </span>
    );
  };

  return (
    <FormControl
      className={classNames(classes.main, className)}
      component="fieldset"
    >
      {label && (
        <FormLabel classes={{ root: classes.label }} component="legend">
          {label}
        </FormLabel>
      )}
      <RadioGroup
        classes={{ root: classes.buttons }}
        value={value}
        onChange={handleChange}
      >
        {options.map(option => (
          <FormControlLabel
            key={`option_item_${option.value}`}
            classes={{
              root: classNames(
                classes.optionLabelWrapper,
                {
                  [classes.disabledChecked]: isDisabled,
                },
                customOptionLabelWrapperClass
              ),
            }}
            label={renderOptionLabel(option)}
            value={option.value}
            disabled={isDisabled}
            control={
              <Radio
                classes={{
                  root: customRadioClass,
                }}
              />
            }
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

CustomRadioGroup.propTypes = {
  className: PropTypes.string,
  customLabelClass: PropTypes.string,
  customOptionLabelWrapperClass: PropTypes.string,
  customRadioClass: PropTypes.string,
  customLabelWrapperClass: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string,
  ]).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

export default CustomRadioGroup;
